import { useState } from 'react';
import {
  Button,
  CreateButton,
  FunctionField,
  Labeled,
  TextField,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
} from 'react-admin';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import IconAdd from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Container,
  Grid,
  Tooltip,
  Typography,
} from '@mui/material';
import { useGateValue } from '@statsig/react-bindings';
import classnames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';

import { useProductsByState } from '@pumpkincare/quotes';
import { GENDER_MAP } from '@pumpkincare/shared';
import { getEmployerTier, getSpecies, getTier } from '@pumpkincare/wellness';

import AddEditPetPlanButton from '../../../../components/AddEditPetPlanButton';
import DateFieldIgnoringZone from '../../../../components/DateFieldIgnoringZone';
import useBooleanInput from '../../../../hooks/useBooleanInput';
import RoutePaths from '../../../../routes';
import { formatCurrency } from '../../../../shared/utils';
import {
  appendBreedName,
  checkCancellation,
  checkPolicyCancellation,
  checkWellnessCancellation,
  getMostRecentPolicyFromPet,
  petHasGroupDiscount,
  petHasValidPlanForAddingPrevent,
  shouldShowWellness,
} from '../../../../shared/utils/petUtils';
import {
  getCurrentPolicy,
  getCurrentWellness,
} from '../../../../shared/utils/productUtils';
import { getWellnessPrice } from '../../../pets/utils/pet-utils';
import { getPolicyState } from '../../utils/customer-utils';
import PetPlanItem from '../pet-plan-item';
import PolicyStatusField from '../policy-status-field';
import EditPetModal from './edit-pet-modal';
import { petListStyle, sxPetListStyle } from './pet-list-style';

function PetPlanWrapper({ petRecord = {} }) {
  const { punks1928WellnessEmployerBenefit } = useFlags();

  const pepSunset = useGateValue('punks-3064-pep-sunset-nb');
  const policyState = getPolicyState(
    petRecord?.latest_active_policy,
    petRecord?.policies
  );

  const [onSuccessRedirect, setOnSuccessRedirect] = useState(() => {});

  const { data: productData } = useProductsByState(policyState);
  const hasValidPlan = petHasValidPlanForAddingPrevent(petRecord);
  const redirect = useRedirect();
  const refresh = useRefresh();
  const userRecord = useRecordContext();
  const [isEditModalOpen, toggleIsEditModalOpen] = useBooleanInput(false);
  const isCAModelLaw = policyState === 'CA';

  const canAddPrevent =
    pepSunset === false &&
    petRecord?.wellness?.status !== 'active' &&
    !checkCancellation(petRecord?.policies) &&
    productData?.can_upsell_prevent &&
    !isCAModelLaw &&
    !petRecord.wellness_plans?.some(
      plan => plan.status === 'pending' || plan.status === 'renew_pending'
    );

  const canAddWellness =
    (!petRecord.wellness || checkWellnessCancellation(petRecord?.wellness)) &&
    !hasValidPlan &&
    !petRecord.wellness_plans?.some(
      plan => plan.status === 'pending' || plan.status === 'renew_pending'
    );

  const canAddInsurance =
    petRecord.wellness && checkPolicyCancellation(petRecord?.policies);

  function redirectToInsuranceCreate(data) {
    refresh();
    redirect('create', RoutePaths.insurance, null, null, {
      pet: data || petRecord,
      user: userRecord,
    });
  }

  function redirectToWellnessCreate(data) {
    refresh();
    redirect('create', RoutePaths.wellness, null, null, {
      pet: data || petRecord,
      user: userRecord,
    });
  }

  function handleCreateButtonClick() {
    if (
      !petRecord.gender ||
      !petRecord.breed_name ||
      petRecord.breed_name === 'N/A'
    ) {
      setOnSuccessRedirect(() => redirectToInsuranceCreate);
      toggleIsEditModalOpen();
    } else {
      redirectToInsuranceCreate();
    }
  }

  function handleCreateWellnessButtonClick() {
    if (
      !petRecord.gender ||
      !petRecord.breed_name ||
      petRecord.breed_name === 'N/A'
    ) {
      setOnSuccessRedirect(() => redirectToWellnessCreate);
      toggleIsEditModalOpen();
    } else {
      redirectToWellnessCreate();
    }
  }

  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: '18px 0 !important',
        justifyContent: 'flex-start',
        alignItems: 'start',
        margin: '0',
      }}
    >
      {canAddPrevent ? (
        <AddEditPetPlanButton hasValidPlan={hasValidPlan} petRecord={petRecord} />
      ) : null}

      {canAddWellness ? (
        <Button onClick={handleCreateWellnessButtonClick}>
          <IconAdd />
          ADD WELLNESS
        </Button>
      ) : null}

      {canAddInsurance ? (
        <>
          {punks1928WellnessEmployerBenefit ? (
            <Button onClick={handleCreateButtonClick}>
              <IconAdd />
              ADD INSURANCE
            </Button>
          ) : (
            <>
              <CreateButton
                label='ADD INSURANCE'
                disabled={!petRecord.breed_name || !petRecord.gender}
                resource={RoutePaths.insurance}
                state={{
                  pet: petRecord,
                  user: userRecord,
                }}
              />
              {(!petRecord.breed_name || !petRecord.gender) && (
                <Typography sx={{ color: '#d32f2f' }}>
                  Please update breed and gender before adding insurance.
                </Typography>
              )}
            </>
          )}
        </>
      ) : null}

      {isEditModalOpen ? (
        <EditPetModal
          record={petRecord}
          onClose={toggleIsEditModalOpen}
          isOpen={isEditModalOpen}
          onSuccess={onSuccessRedirect}
        />
      ) : null}
    </Container>
  );
}

PetPlanWrapper.propTypes = {
  petRecord: PropTypes.shape({
    latest_active_policy: PropTypes.object,
    policies: PropTypes.array,
    wellness: PropTypes.object,
    breed_name: PropTypes.string,
    gender: PropTypes.string,
  }),
};

export default function PetList({ hasCancellations, petListData }) {
  const { punks1928WellnessEmployerBenefit, punks2235VetWellnessQtyUtilization } =
    useFlags();

  const classes = petListStyle();
  const redirect = useRedirect();
  const notify = useNotify();
  const userRecord = useRecordContext();
  const PET_HEADERS = [
    'PRODUCT',
    'STATUS',
    'COST',
    'VERSION',
    'EFFECTIVE DATE',
    'NEXT CHARGE DATE',
    'LAPSED DATE',
    'RENEWAL STATUS',
    'DISCOUNT',
  ];

  const AVATAR_PLACEHOLDERS = {
    dog: '/assets/images/dog_placeholder.svg',
    cat: '/assets/images/cat_placeholder.svg',
  };

  const { id: user_id, is_charged_annually: isChargedAnnually } = userRecord;

  function renewalStatus(record, type = 'insurance') {
    const renewStatus =
      type === 'insurance' ? record.renew_status : record.wellness_renew_status;

    return (
      <span
        className={classnames({
          [classes.renewalToday]: renewStatus === 'Renewal Today',
          [classes.renewalUpcoming]: renewStatus === 'Upcoming Renewal',
        })}
      >
        {renewStatus}
      </span>
    );
  }

  function formatLabelSize(label) {
    let size;

    switch (label) {
      case 'NEXT CHARGE DATE':
      case 'EFFECTIVE DATE':
        size = 1.5;
        break;
      case 'VERSION':
        size = 1.6;
        break;
      case 'PRODUCT':
        size = 0.9;
        break;
      case 'STATUS':
        size = 1.5;
        break;
      case 'RENEWAL STATUS':
        size = 1.6;
        break;
      case 'GROUP DISCOUNT':
        size = 1;
        break;
      case 'DISCOUNT':
        size = 1;
        break;
      default:
        size = 1.2;
    }

    return size;
  }

  function renderClaimButton(petId, isEstimate) {
    return (
      <CreateButton
        label={`CREATE ${isEstimate ? 'ESTIMATE' : 'CLAIM'}`}
        icon={null}
        sx={{
          border: '1px solid rgba(83, 104, 245, 1)',
          width: '100%',
          textAlign: 'center',
          marginBottom: isEstimate ? '16px' : 0,
        }}
        data-testid={`create-${isEstimate ? 'estimate' : 'claim'}-btn`}
        resource={RoutePaths.claims}
        state={{
          customerId: user_id,
          petId,
          isEstimate,
        }}
      />
    );
  }

  function renderDiscount(petRecord) {
    const hasDiscount = petHasGroupDiscount(petRecord);
    const policy = getMostRecentPolicyFromPet(petRecord);

    if (hasDiscount) {
      return policy.organization_org_type
        ? policy.organization_org_type.toUpperCase()
        : 'Not Applied';
    }

    return hasDiscount ? 'Applied' : 'Not Applied';
  }

  function renderPetTooltip(petRecord, managedBy) {
    if (punks1928WellnessEmployerBenefit && petRecord.wellness.group_enrolled) {
      return (
        <Tooltip
          placement='bottom'
          title={`Wellness ${managedBy}: ${petRecord.third_party_entity_name}`}
          className={classes.thirdParty}
        >
          <InfoOutlinedIcon
            sx={{
              lineHeight: '20px',
              fontSize: '12px',
              fontWeight: '500',
            }}
            fontSize='inherit'
          />
          <Typography
            sx={{
              lineHeight: '20px',
              fontSize: '12px',
              fontWeight: '500',
            }}
            variant='subtitle2'
          >
            Third Party
          </Typography>
        </Tooltip>
      );
    } else if (punks2235VetWellnessQtyUtilization && petRecord?.wellness?.vet) {
      return (
        <Tooltip
          placement='bottom'
          title={`${petRecord?.wellness?.vet?.vet_name} customizes plans and tracks usage. Pumpkin handles customer support and billing.`}
          className={classes.thirdParty}
        >
          <InfoOutlinedIcon
            sx={{
              lineHeight: '20px',
              fontSize: '12px',
              fontWeight: '500',
            }}
            fontSize='inherit'
          />
          <Typography
            sx={{
              lineHeight: '20px',
              fontSize: '12px',
              fontWeight: '500',
            }}
            variant='subtitle2'
          >
            Vet
          </Typography>
        </Tooltip>
      );
    } else {
      return null;
    }
  }

  function getCurrentEnrollmentType(petRecord) {
    return petRecord.enrollment_type === 'employer'
      ? 'plan(s) managed by employer'
      : 'managed by vendor';
  }

  function isVetWellness(petRecord) {
    return punks2235VetWellnessQtyUtilization && petRecord?.wellness?.vet;
  }

  function isVetWellnessOnly(petRecord, policy) {
    return isVetWellness(petRecord) && !policy;
  }

  return petListData.map(petRecord => {
    const policy = getCurrentPolicy(petRecord?.policies);
    const managedBy = getCurrentEnrollmentType(petRecord);

    return (
      <Box key={petRecord.id}>
        <Accordion
          sx={{
            boxShadow: 'none',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            '&:before': {
              height: '0px',
              borderBottom: '1px',
            },
            '&.Mui-expanded': {
              margin: '0px',
              marginBottom: '0px',
            },
            marginBottom: '0px',
          }}
          key={petRecord.id}
          defaultExpanded={
            policy?.status === 'active' ||
            getCurrentWellness(petRecord)?.status === 'active'
          }
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls='panel1-content'
            id='panel1-header'
            sx={{
              alignItems: 'center',
              '&.Mui-expanded': {
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
              },
              '& .MuiAccordionSummary-expandIconWrapper': {
                padding: '0 16px',
              },
            }}
          >
            <Grid
              container
              direction='row'
              sx={{
                paddingBottom: '16px',
              }}
              alignItems='center'
            >
              <Grid xs={2.5} item>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div>
                    <Avatar
                      sx={{
                        width: '80px',
                        height: '80px',
                      }}
                      alt={petRecord?.pet_photo?.location.split('/')[-1]}
                      src={
                        petRecord?.pet_photo?.location ||
                        AVATAR_PLACEHOLDERS[petRecord.species]
                      }
                    />
                  </div>
                  <div style={{ marginLeft: '8px' }}>
                    <Typography variant='h5'>{petRecord.name}</Typography>
                    <Typography
                      variant='subtitle2'
                      sx={{ color: '#5368F5' }}
                      onClick={() => redirect(`/pets/${petRecord.id}/show`)}
                    >
                      View Pet Details
                    </Typography>
                  </div>
                </div>
              </Grid>
              <Grid xs={1.6} item>
                <Labeled
                  sx={{
                    '& .RaLabeled-label': sxPetListStyle.labelCell,
                  }}
                  label='PET ID'
                >
                  <FunctionField
                    label='ID'
                    variant='body1'
                    sx={{ color: 'black' }}
                    onClick={e => {
                      e.stopPropagation();
                    }}
                    render={() => {
                      return (
                        <CopyToClipboard
                          text={petRecord.pumpkin_id}
                          onCopy={() => {
                            notify(`${petRecord.pumpkin_id} copied`, {});
                          }}
                        >
                          <span style={{ cursor: 'pointer' }}>
                            {petRecord.pumpkin_id}
                          </span>
                        </CopyToClipboard>
                      );
                    }}
                  />
                </Labeled>
              </Grid>
              <Grid xs={1} item>
                <Labeled
                  sx={{
                    '& .RaLabeled-label': sxPetListStyle.labelCell,
                  }}
                  label='SPECIES'
                >
                  <TextField
                    record={petRecord}
                    sx={{ color: 'black', textTransform: 'capitalize' }}
                    source='species'
                    variant='body1'
                  />
                </Labeled>
              </Grid>
              <Grid xs={1} item>
                <Labeled
                  sx={{
                    '& .RaLabeled-label': sxPetListStyle.labelCell,
                  }}
                  label='GENDER'
                >
                  <Typography
                    sx={{
                      color: '#000000',
                    }}
                    variant='subtitle2'
                  >
                    {GENDER_MAP[petRecord?.gender] || 'N/A'}
                  </Typography>
                </Labeled>
              </Grid>
              <Grid xs={3} item>
                <Labeled
                  sx={{
                    '& .RaLabeled-label': sxPetListStyle.labelCell,
                  }}
                  label='BREED'
                >
                  <TextField
                    record={appendBreedName(petRecord)}
                    sx={{ color: 'black' }}
                    source='breed_name'
                    variant='body1'
                  />
                </Labeled>
              </Grid>
              <Grid xs={1.4} item>
                <Labeled
                  sx={{
                    '& .RaLabeled-label': sxPetListStyle.labelCell,
                  }}
                  label='AGE'
                >
                  <TextField
                    record={petRecord}
                    sx={{ color: 'black' }}
                    source='age'
                    variant='body1'
                  />
                </Labeled>
              </Grid>
              {isVetWellnessOnly(petRecord, policy) ? null : (
                <Grid xs={1.5} item>
                  {policy ? renderClaimButton(petRecord.id, true) : null}
                  {renderClaimButton(petRecord.id, false)}
                </Grid>
              )}
            </Grid>
          </AccordionSummary>
          <AccordionDetails sx={{ paddingTop: '24px' }}>
            <Grid container direction='row' alignItems='center'>
              {PET_HEADERS.map(label => (
                <Grid xs={formatLabelSize(label)} item key={label}>
                  <Typography
                    sx={{
                      color: '#6B717E',
                    }}
                    variant='overline'
                  >
                    {label}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            {shouldShowWellness(petRecord) && petRecord.wellness ? (
              <Grid container direction='row' alignItems='baseline'>
                <Grid xs={0.9} item>
                  <Typography
                    sx={{
                      color: '#000000',
                    }}
                    variant='subtitle2'
                  >
                    Wellness
                  </Typography>
                  {renderPetTooltip(petRecord, managedBy)}
                </Grid>

                {/*STATUS*/}
                <Grid xs={1.5} item>
                  <Typography
                    sx={{ color: '#000000', textTransform: 'capitalize' }}
                    variant='subtitle2'
                    className={classnames({
                      [classes.cancelledStatus]:
                        petRecord.wellness.status === 'cancelled',
                    })}
                  >
                    {petRecord.wellness.status}
                  </Typography>
                </Grid>

                {/*COST*/}
                <Grid xs={1.2} item>
                  <Typography
                    sx={{
                      color: '#000000',
                    }}
                    variant='subtitle2'
                  >
                    {formatCurrency(
                      getWellnessPrice(petRecord?.wellness, isChargedAnnually),
                      { areCents: false }
                    )}
                  </Typography>
                </Grid>

                {/*VERSION*/}
                {isVetWellness(petRecord) ? (
                  <Grid xs={1.6} item>
                    <Typography
                      sx={{
                        color: '#000000',
                      }}
                      variant='subtitle2'
                    >
                      {petRecord.wellness.program_id}
                    </Typography>
                  </Grid>
                ) : (
                  <Grid xs={1.6} item>
                    <Typography
                      sx={{
                        color: '#000000',
                      }}
                      variant='subtitle2'
                    >
                      {`${getSpecies(petRecord.wellness.tier)} ${
                        petRecord.wellness.group_enrolled
                          ? getEmployerTier(
                              petRecord.wellness.tier,
                              petRecord.wellness.program_id
                            )
                          : getTier(
                              petRecord.wellness.tier,
                              petRecord.wellness.program_id
                            )
                      }`}
                    </Typography>
                  </Grid>
                )}

                {/*EFFECTIVE DATE*/}
                <Grid xs={1.5} item>
                  <Typography
                    sx={{
                      color: '#000000',
                      fontSize: '0.875rem',
                    }}
                    variant='subtitle2'
                  >
                    <DateFieldIgnoringZone
                      source='start_date'
                      record={petRecord.wellness}
                      style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        lineHeight: '22px',
                        letterspacing: '0.1px',
                      }}
                    />
                  </Typography>
                </Grid>

                {/*NEXT CHARGE DATE*/}
                <Grid xs={1.5} item>
                  <Typography
                    sx={{
                      color: '#000000',
                    }}
                    variant='subtitle2'
                  >
                    <DateFieldIgnoringZone
                      source='wellness_next_charge_date'
                      record={petRecord}
                      style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        lineHeight: '22px',
                        letterspacing: '0.1px',
                      }}
                    />
                  </Typography>
                </Grid>
                <Grid xs={1.2} item>
                  <Typography
                    sx={{
                      color: '#000000',
                    }}
                    variant='subtitle2'
                  >
                    {petRecord.wellness?.lapsed_since ? (
                      <DateFieldIgnoringZone
                        source='lapsed_since'
                        record={petRecord.wellness}
                        style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          lineHeight: '22px',
                          letterspacing: '0.1px',
                        }}
                      />
                    ) : (
                      ''
                    )}
                  </Typography>
                </Grid>
                <Grid xs={1.6} item>
                  <Typography
                    sx={{
                      color: '#000000',
                    }}
                    variant='subtitle2'
                  >
                    {renewalStatus(petRecord, 'wellness')}
                  </Typography>
                </Grid>
              </Grid>
            ) : null}

            {policy ? (
              <Grid container direction='row' alignItems='center'>
                <Grid xs={0.9} item>
                  <Typography
                    sx={{
                      color: '#000000',
                    }}
                    variant='subtitle2'
                  >
                    Insurance
                  </Typography>
                </Grid>
                <Grid xs={1.5} item>
                  <PolicyStatusField
                    hasCancellations={hasCancellations}
                    policy={policy}
                    userId={petRecord?.user_id}
                  />
                </Grid>
                <Grid xs={1.2} item>
                  <Typography
                    sx={{
                      color: '#000000',
                    }}
                    variant='subtitle2'
                  >
                    {formatCurrency(policy.premium, {
                      areCents: false,
                    })}
                  </Typography>
                </Grid>
                <Grid xs={1.6} item>
                  <Typography
                    sx={{
                      color: '#000000',
                    }}
                    variant='subtitle2'
                  >
                    {policy.program_id}
                  </Typography>
                </Grid>
                <Grid xs={1.5} item>
                  <Typography
                    sx={{
                      color: '#000000',
                    }}
                    variant='subtitle2'
                  >
                    <DateFieldIgnoringZone
                      source='policy_effective_date'
                      record={policy}
                      style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        lineHeight: '22px',
                        letterspacing: '0.1px',
                      }}
                    />
                  </Typography>
                </Grid>
                <Grid xs={1.5} item>
                  <Typography
                    sx={{
                      color: '#000000',
                    }}
                    variant='subtitle2'
                  >
                    <DateFieldIgnoringZone
                      source='next_charge_date'
                      record={petRecord}
                      style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        lineHeight: '22px',
                        letterspacing: '0.1px',
                      }}
                    />
                  </Typography>
                </Grid>
                <Grid xs={1.2} item>
                  <Typography
                    sx={{
                      color: '#000000',
                    }}
                    variant='subtitle2'
                  >
                    {policy.lapsed_since ? (
                      <DateFieldIgnoringZone
                        source='lapsed_since'
                        record={policy}
                        style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          lineHeight: '22px',
                          letterspacing: '0.1px',
                        }}
                      />
                    ) : (
                      ''
                    )}
                  </Typography>
                </Grid>
                <Grid xs={1.6} item>
                  <Typography
                    sx={{
                      color: '#000000',
                    }}
                    variant='subtitle2'
                  >
                    {renewalStatus(petRecord, 'insurance')}
                  </Typography>
                </Grid>
                <Grid xs={1} item>
                  <Typography
                    sx={{
                      color: '#000000',
                    }}
                    variant='subtitle2'
                  >
                    {renderDiscount(petRecord)}
                  </Typography>
                </Grid>
              </Grid>
            ) : null}
            <PetPlanItem petRecord={petRecord} />
          </AccordionDetails>
        </Accordion>
        <Grid
          container
          direction='row'
          sx={{
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderTop: 0,
            paddingLeft: '16px',
          }}
          alignItems='center'
        >
          <PetPlanWrapper petRecord={petRecord} />
        </Grid>
      </Box>
    );
  });
}
